import { useState } from 'react'

interface Level {
  id: string
  name: string
  code: string
  order: number
  description: string
  expectations: string[]
}

interface JobFamily {
  id: string
  name: string
  description: string
  levels: Level[]
  isExpanded?: boolean
}

// Mock data - in production this comes from API
const MOCK_JOB_FAMILIES: JobFamily[] = [
  {
    id: '1',
    name: 'Engineering',
    description: 'Software development, infrastructure, and technical roles',
    levels: [
      {
        id: 'e1',
        name: 'Junior Engineer',
        code: 'E1',
        order: 1,
        description: 'Entry-level individual contributor with foundational skills',
        expectations: [
          'Completes well-defined tasks with guidance',
          'Learns codebase and team practices',
          'Asks questions and seeks feedback',
          'Writes code that meets basic quality standards',
        ],
      },
      {
        id: 'e2',
        name: 'Engineer',
        code: 'E2',
        order: 2,
        description: 'Competent individual contributor working independently',
        expectations: [
          'Owns features end-to-end with minimal guidance',
          'Writes clean, tested, maintainable code',
          'Participates in code reviews',
          'Debugs and resolves issues independently',
        ],
      },
      {
        id: 'e3',
        name: 'Senior Engineer',
        code: 'E3',
        order: 3,
        description: 'Experienced IC who leads projects and mentors others',
        expectations: [
          'Leads design of complex features',
          'Mentors junior engineers',
          'Identifies and addresses technical debt',
          'Influences team technical decisions',
        ],
      },
      {
        id: 'e4',
        name: 'Staff Engineer',
        code: 'E4',
        order: 4,
        description: 'Technical leader with org-wide impact',
        expectations: [
          'Drives architectural decisions across teams',
          'Solves ambiguous, high-impact problems',
          'Sets technical direction for organization',
          'Recognized expert in domain areas',
        ],
      },
      {
        id: 'e5',
        name: 'Principal Engineer',
        code: 'E5',
        order: 5,
        description: 'Distinguished contributor shaping company strategy',
        expectations: [
          'Company-wide technical leadership',
          'Industry-recognized expertise',
          'Shapes long-term technical vision',
          'Influences product and business strategy',
        ],
      },
    ],
  },
  {
    id: '2',
    name: 'Product',
    description: 'Product management and strategy roles',
    levels: [
      {
        id: 'p1',
        name: 'Associate PM',
        code: 'P1',
        order: 1,
        description: 'Entry-level product manager learning the craft',
        expectations: [
          'Gathers and documents requirements',
          'Writes clear user stories and specs',
          'Supports senior PMs on projects',
          'Learns customer research methods',
        ],
      },
      {
        id: 'p2',
        name: 'Product Manager',
        code: 'P2',
        order: 2,
        description: 'Owns product areas independently',
        expectations: [
          'Owns roadmap for product area',
          'Runs discovery and prioritization',
          'Works effectively with engineering',
          'Measures and iterates on outcomes',
        ],
      },
      {
        id: 'p3',
        name: 'Senior PM',
        code: 'P3',
        order: 3,
        description: 'Leads complex products with cross-functional impact',
        expectations: [
          'Drives strategy for major product lines',
          'Mentors junior PMs',
          'Influences company-level priorities',
          'Builds strong stakeholder relationships',
        ],
      },
    ],
  },
  {
    id: '3',
    name: 'Design',
    description: 'Product design, UX research, and visual design roles',
    levels: [
      {
        id: 'd1',
        name: 'Junior Designer',
        code: 'D1',
        order: 1,
        description: 'Entry-level designer developing core skills',
        expectations: [
          'Executes designs from established patterns',
          'Supports usability testing',
          'Maintains design system components',
          'Learns design tools and processes',
        ],
      },
      {
        id: 'd2',
        name: 'Product Designer',
        code: 'D2',
        order: 2,
        description: 'Designs features independently with quality',
        expectations: [
          'Owns design for features end-to-end',
          'Conducts user research',
          'Creates and iterates on prototypes',
          'Collaborates effectively with PMs and engineers',
        ],
      },
      {
        id: 'd3',
        name: 'Senior Designer',
        code: 'D3',
        order: 3,
        description: 'Design leader influencing product direction',
        expectations: [
          'Leads design for major initiatives',
          'Shapes design strategy and vision',
          'Mentors other designers',
          'Advocates for user-centered thinking',
        ],
      },
    ],
  },
]

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  )
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  )
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  )
}

function LevelRow({ level, onEdit }: { level: Level; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border-b border-surface-100 last:border-b-0">
      <div className="flex items-center gap-4 px-4 py-3 hover:bg-surface-50 transition-colors">
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-surface-400 hover:text-surface-600 transition-colors"
        >
          <ChevronRightIcon className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        
        <div className="w-12 text-center">
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-surface-100 text-xs font-mono font-medium text-surface-700">
            {level.code}
          </span>
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-surface-900">{level.name}</p>
          <p className="text-sm text-surface-500 truncate">{level.description}</p>
        </div>
        
        <button
          onClick={onEdit}
          className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      </div>
      
      {expanded && (
        <div className="px-4 py-3 pl-16 bg-surface-50 border-t border-surface-100">
          <p className="text-sm font-medium text-surface-700 mb-2">Level expectations:</p>
          <ul className="space-y-1.5">
            {level.expectations.map((exp, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-surface-600">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-surface-400 shrink-0" />
                {exp}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function JobFamilyCard({ family, onEdit }: { family: JobFamily; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(true)

  return (
    <div className="border border-surface-200 rounded-xl overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-surface-50 border-b border-surface-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-surface-400 hover:text-surface-600 transition-colors"
          >
            <ChevronRightIcon className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
          <div className="w-9 h-9 rounded-lg bg-brand-100 flex items-center justify-center">
            <FolderIcon className="w-5 h-5 text-brand-600" />
          </div>
          <div>
            <h3 className="font-medium text-surface-900">{family.name}</h3>
            <p className="text-sm text-surface-500">{family.levels.length} levels defined</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
            title="Edit job family"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add level
          </button>
        </div>
      </div>

      {/* Levels */}
      {expanded && (
        <div>
          {family.levels.map((level) => (
            <LevelRow key={level.id} level={level} onEdit={() => {}} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function RoleLevelSettings() {
  const [families] = useState(MOCK_JOB_FAMILIES)

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">Role & Level Framework</h2>
            <p className="mt-1 text-surface-600">
              Define job families and the levels within each. These determine how candidates are matched and calibrated.
            </p>
          </div>
          <button
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Add job family
          </button>
        </div>
      </div>

      {/* Info callout */}
      <div className="p-6 sm:p-8 bg-surface-50">
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="flex gap-3">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-surface-900">Job Families</p>
              <p className="text-sm text-surface-600">Organize roles by function (Engineering, Product, etc.)</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-surface-900">Levels</p>
              <p className="text-sm text-surface-600">Define progression from junior to senior</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-surface-900">Expectations</p>
              <p className="text-sm text-surface-600">What to look for at each level</p>
            </div>
          </div>
        </div>
      </div>

      {/* Job families list */}
      <div className="p-6 sm:p-8">
        <div className="space-y-6">
          {families.map((family) => (
            <JobFamilyCard key={family.id} family={family} onEdit={() => {}} />
          ))}
        </div>
      </div>

      {/* Empty state */}
      {families.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mx-auto mb-4">
            <FolderIcon className="w-6 h-6 text-surface-400" />
          </div>
          <h3 className="font-medium text-surface-900 mb-1">No job families defined</h3>
          <p className="text-surface-500 text-sm mb-6 max-w-sm mx-auto">
            Create job families to organize your roles and define level expectations for consistent evaluation.
          </p>
          <button
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add first job family
          </button>
        </div>
      )}
    </div>
  )
}

